import React, { Dispatch, SetStateAction } from "react";

import {
  IgCardSelect,
  IgCheckbox,
  IgLink,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  AnalyticsSelectedProductType,
  AnalyticsViewTypeFe,
  GenericExperienceFe,
} from "@intelligems/ig-types";
import { FormControlLabel, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { AnalyticsSelectedProductPickerDialog } from "@src/features/analytics/analyticsFilters/AnalyticsSelectedProductPickerDialog";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useDispatch } from "@src/store";
import { openSelectedAnalyticsProductPicker } from "@src/store/slices/productPicker/thunks";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";

interface AnalyticsViewTypeFilterProps {
  currentExperience?: GenericExperienceFe;
  analyticsViewType: AnalyticsViewTypeFe;
  isTestProductsDefaultChange: boolean;
  setIsTestProductsDefaultChange: Dispatch<SetStateAction<boolean>>;
  onStringFilterChange: (
    newValue: AnalyticsViewTypeFe,
    filterKey: string
  ) => void;
  selectedAnalyticsProducts: AnalyticsSelectedProductType[];
  onSelectedAnalyticsProductsChange: (
    products: ProductPickerSelectedProduct[]
  ) => void;
  hideTargetProductOption: boolean;
}
export const AnalyticsViewTypeFilter = ({
  currentExperience,
  analyticsViewType,
  isTestProductsDefaultChange,
  setIsTestProductsDefaultChange,
  onStringFilterChange,
  selectedAnalyticsProducts,
  onSelectedAnalyticsProductsChange,
  hideTargetProductOption,
}: AnalyticsViewTypeFilterProps) => {
  const dispatch = useDispatch();
  const { isReadOnly } = useReadOnlyMode();

  const analyticsViewTypesList: {
    key: string;
    description: string;
    value: AnalyticsViewTypeFe;
    tooltip?: string;
  }[] = [
    {
      key: "All Orders",
      description:
        "Recommended if test products are substitutes for other products in your store or constitute a large portion (>40%) of your store's revenue.",
      value: "sitewide_orders",
    },
    {
      key: `Orders with ${currentExperience?.testTypes.hasTestCheckoutBlocks ? "Checkout Upsells" : currentExperience?.testTypes.hasTestOnsiteUpsell ? "an Onsite Upsell" : "Target Products"}`,
      description: currentExperience?.testTypes.hasTestCheckoutBlocks
        ? "Include orders that contain at least one product added via your checkout upsell"
        : currentExperience?.testTypes.hasTestOnsiteUpsell
          ? "Include only orders that contain at least one Onsite Upsell"
          : "Include only orders that contain at least one of your target products",
      value: "test_orders_only",
      tooltip:
        currentExperience?.testTypes?.hasTestCheckoutBlocks ||
        currentExperience?.testTypes?.hasTestOnsiteUpsell
          ? undefined
          : `Target products are automatically set to your price test or offer products. For other test types, you can manually define target products in the test setup.`,
    },
    {
      key: "Orders with Specific Products",
      description:
        "Include only orders that contain at least one of the products defined here",
      value: "specific_products",
    },
  ];

  const selectedCard = (viewType: string): boolean => {
    return (
      analyticsViewType === viewType ||
      (viewType === "sitewide_orders" && analyticsViewType === "none")
    );
  };

  const onSelect = (value: AnalyticsViewTypeFe) => {
    onStringFilterChange(value, "analyticsViewType");
    if (value === "sitewide_orders" || value === "test_orders_only") {
      onSelectedAnalyticsProductsChange([]);
    } else {
      void dispatch(
        openSelectedAnalyticsProductPicker(selectedAnalyticsProducts)
      );
    }
  };

  if (currentExperience) {
    return (
      <Stack spacing={2} direction={"column"}>
        <AnalyticsSelectedProductPickerDialog
          selectedAnalyticsProductCount={selectedAnalyticsProducts.length}
          onSelectedAnalyticsProductsChange={onSelectedAnalyticsProductsChange}
          onUpdateDialogFilter={() => {
            onStringFilterChange("sitewide_orders", "analyticsViewType");
          }}
        />
        <IgCardSelect
          options={analyticsViewTypesList
            .filter((list) =>
              hideTargetProductOption ? list.value !== "test_orders_only" : true
            )
            .filter((list) =>
              isReadOnly ? list.value !== "specific_products" : true
            )
            .map((viewType) => ({
              description: viewType.description,
              key: viewType.key,
              title: viewType.key,
              tooltip: viewType.tooltip,
              value: viewType.value,
            }))}
          value={analyticsViewType}
          onChange={onSelect}
          getOptionSelected={(option) => selectedCard(option.value)}
        />
        {!isReadOnly &&
          currentExperience &&
          currentExperience.category === "experiment" &&
          currentExperience.experienceAnalysis.analyticsViewType !==
            analyticsViewType &&
          analyticsViewType !== "specific_products" &&
          !(analyticsViewType === "test_orders_only") && (
            <Grid>
              <FormControlLabel
                control={
                  <IgCheckbox
                    size={"small"}
                    checked={isTestProductsDefaultChange}
                    onChange={(event) => {
                      setIsTestProductsDefaultChange(event.target.checked);
                    }}
                  />
                }
                label={
                  <IgTypography variant={"body2"}>Set as Default</IgTypography>
                }
              />
            </Grid>
          )}
        {analyticsViewType === "specific_products" &&
        selectedAnalyticsProducts.length > 0 ? (
          <Grid>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              columnGap={2}
            >
              <IgLink
                onClick={() => {
                  onSelect("sitewide_orders");
                }}
              >
                Clear product{selectedAnalyticsProducts.length > 1 ? "s" : ""}
              </IgLink>
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                columnGap={2}
              >
                <IgTypography>
                  {selectedAnalyticsProducts.length} product
                  {selectedAnalyticsProducts.length > 1 ? "s" : ""} selected
                </IgTypography>
                <IgLink
                  onClick={() => {
                    void dispatch(
                      openSelectedAnalyticsProductPicker(
                        selectedAnalyticsProducts
                      )
                    );
                  }}
                >
                  Edit product{selectedAnalyticsProducts.length > 1 ? "s" : ""}
                </IgLink>
              </Stack>
            </Stack>
          </Grid>
        ) : null}
      </Stack>
    );
  }
  return null;
};
